import { typedRoute } from "@d12labs/client";
import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { KPI } from "@d12labs/ui/kpi";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import type { jobsAdminRouteDefs } from "@/api/jobs-admin-routes";

/** Typed callers derived from the server defs (type-only import, erased at build). */
const jobsAdminApi = {
	overview: typedRoute<typeof jobsAdminRouteDefs.overview>()("GET", "/api/admin/jobs"),
	retry: typedRoute<typeof jobsAdminRouteDefs.retry>()("POST", "/api/admin/jobs/:id/retry"),
};

type Overview = Awaited<ReturnType<typeof jobsAdminApi.overview>>;
type JobRow = Overview["deadLetters"][number];

const when = (d: string | Date | null) => (d ? new Date(d).toLocaleString() : "—");

function JobsTable({
	jobs,
	emptyLabel,
	onRetry,
	retryingId,
}: {
	jobs: JobRow[];
	emptyLabel: string;
	onRetry: (id: string) => void;
	retryingId: string | null;
}) {
	if (jobs.length === 0) {
		return <p className="text-sm text-muted-foreground py-3">{emptyLabel}</p>;
	}
	const columns = [
		{ label: "Type", width: "1.2fr" },
		{ label: "Attempts", width: "0.5fr" },
		{ label: "Last error", width: "2fr" },
		{ label: "Updated", width: "1fr" },
		{ label: "", width: "auto", align: "right" as const },
	];
	return (
		<List>
			<ListHeader columns={columns} />
			{jobs.map((j) => (
				<ListRow
					key={j.id}
					cells={[
						{ value: j.type, mono: true, width: "1.2fr" },
						{ value: `${j.attempts}/${j.maxAttempts}`, width: "0.5fr" },
						{
							value: (
								<span className="truncate block" title={j.lastError ?? ""}>
									{j.lastError ?? "—"}
								</span>
							),
							muted: true,
							width: "2fr",
						},
						{ value: when(j.updatedAt), muted: true, width: "1fr" },
						{
							value: (
								<Button
									size="sm"
									variant="outline"
									disabled={retryingId === j.id}
									onClick={() => onRetry(j.id)}
								>
									<RefreshCw className="size-3.5" />
									Retry
								</Button>
							),
							width: "auto",
							align: "right" as const,
						},
					]}
				/>
			))}
		</List>
	);
}

export function JobsAdminPage() {
	const queryClient = useQueryClient();
	const overview = useQuery({
		queryKey: ["admin", "jobs"],
		queryFn: () => jobsAdminApi.overview(),
		refetchInterval: 15_000,
	});

	const retry = useMutation({
		mutationFn: (id: string) => jobsAdminApi.retry({ params: { id } }),
		onSuccess: () => {
			toast.success("Job requeued");
			queryClient.invalidateQueries({ queryKey: ["admin", "jobs"] });
		},
		meta: { errorMessage: "Retry failed" },
	});

	// The API 403s for non-admins; apiFetch throws, so render the no-access state.
	if (overview.isError) {
		return (
			<PageBody>
				<PageHeader icon={ListChecks} title="Jobs" />
				<p className="text-sm text-muted-foreground">
					You don't have access to this page (admin only).
				</p>
			</PageBody>
		);
	}

	const data = overview.data;
	const counts = data?.counts ?? {};

	return (
		<PageBody>
			<PageHeader
				icon={ListChecks}
				title="Jobs"
				subtitle="Background queue health — dead letters, stuck jobs, and retry."
			/>

			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
				<KPI label="Pending" value={String(counts.pending ?? 0)} />
				<KPI label="Processing" value={String(counts.processing ?? 0)} />
				<KPI label="Completed" value={String(counts.completed ?? 0)} />
				<KPI label="Failed" value={String(counts.failed ?? 0)} />
			</div>

			<section className="space-y-2">
				<h2 className="text-sm font-medium flex items-center gap-2">
					Dead letters
					<Badge>{data?.deadLetters.length ?? 0}</Badge>
				</h2>
				<p className="text-xs text-muted-foreground">
					Terminally failed jobs (all attempts exhausted). Retry requeues with a fresh attempt
					cycle.
				</p>
				<JobsTable
					jobs={data?.deadLetters ?? []}
					emptyLabel="No dead-lettered jobs."
					onRetry={(id) => retry.mutate(id)}
					retryingId={retry.isPending ? (retry.variables ?? null) : null}
				/>
			</section>

			<section className="space-y-2">
				<h2 className="text-sm font-medium flex items-center gap-2">
					Stuck
					<Badge>{data?.stuck.length ?? 0}</Badge>
				</h2>
				<p className="text-xs text-muted-foreground">
					Processing for {Math.round((data?.stuckAfterMs ?? 0) / 60_000)}+ minutes with no heartbeat
					— the worker likely died mid-job. Retry only if the handler is safe to re-run.
				</p>
				<JobsTable
					jobs={data?.stuck ?? []}
					emptyLabel="No stuck jobs."
					onRetry={(id) => retry.mutate(id)}
					retryingId={retry.isPending ? (retry.variables ?? null) : null}
				/>
			</section>
		</PageBody>
	);
}
